import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiBaseUrl, authBaseUrl, wppBaseUrl } from '../../config/runtime';
import { getSupportStatusLabel } from '../../utils/supportStatus';
import './ChatWindow.css';

const ChatWindow = ({ chat, socket, onMarkAsRead, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [newTemplate, setNewTemplate] = useState('');
  const [editTemplateIndex, setEditTemplateIndex] = useState(null);
  const templateRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [customer, setCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    document: '',
    notes: '',
    address: '',
    company_id: '',
    company_name: '',
    company_document: '',
  });
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [historyNotes, setHistoryNotes] = useState([]);
  const [newHistoryNote, setNewHistoryNote] = useState('');
  const [ticketAction, setTicketAction] = useState({
    category: '',
    resolution_summary: '',
    protocol_details: '',
    gc_reason: '',
  });
  const [protocols, setProtocols] = useState([]);
  const [actionLoading, setActionLoading] = useState('');
  const [actionFeedback, setActionFeedback] = useState('');
  const [showTransferPanel, setShowTransferPanel] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [transferUserId, setTransferUserId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferFeedback, setTransferFeedback] = useState('');

  const [userScrolled, setUserScrolled] = useState(false);

  const [attendantName, setAttendantName] = useState('');

  useEffect(() => {
    const storedAttendantName = localStorage.getItem('attendantName');
    setAttendantName(storedAttendantName || 'Desconhecido');
  }, []);

  useEffect(() => {
    axios.get(`${wppBaseUrl}/templates`)
      .then(response => {
        setTemplates(response.data);
      })
      .catch(() => {});
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/companies`);
      setCompanies(response.data || []);
    } catch (error) {
      setCompanies([]);
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!showTransferPanel || !chat) {
      return;
    }
    const loadAssignableUsers = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAssignableUsers([]);
        setTransferFeedback('Sessao invalida. Faca login novamente.');
        return;
      }
      try {
        const companyCode = localStorage.getItem('companyCode');
        const response = await axios.get(`${authBaseUrl}/users/assignable`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(companyCode ? { 'X-Company-Code': companyCode } : {})
          }
        });
        const rows = (response.data || []).filter((user) => user?.email);
        setAssignableUsers(rows);
        const currentUserId = localStorage.getItem('userId');
        const suggestedTarget = rows.find((user) => user.email !== currentUserId)?.email || rows[0]?.email || '';
        setTransferUserId((prev) => prev || suggestedTarget);
      } catch (error) {
        setAssignableUsers([]);
        setTransferFeedback(error?.response?.data?.message || 'Falha ao carregar atendentes para transferencia.');
      }
    };
    loadAssignableUsers();
  }, [showTransferPanel, chat]);

  useEffect(() => {
    if (chat) {
      const companyCode = localStorage.getItem('companyCode');
      socket.emit('get_messages', {
        chatId: chat.chatId,
        ticketNumber: chat.TicketNumber,
        company_code: companyCode || undefined
      });

      socket.on('messages', (messages) => {
        const filtered = (messages || []).filter(
          (msg) => !msg.ticketNumber || msg.ticketNumber === chat.TicketNumber
        );
        setMessages(filtered);
        onMarkAsRead(chat.chatId);
      });

      socket.on('new_message', (message) => {
        if (message.chatId !== chat.chatId) return;
        if (message.ticketNumber && message.ticketNumber !== chat.TicketNumber) return;
        const normalized = {
          ...message,
          timestamp: message.timestamp || new Date().toISOString(),
        };
        setMessages((prev) => [...prev, normalized]);
      });

      socket.on('new_internal_note', (note) => {
        if (note.chatId !== chat.chatId) return;
        setMessages((prev) => [...prev, note]);
      });

      return () => {
        socket.off('messages');
        socket.off('new_message');
        socket.off('new_internal_note');
      };
    }
  }, [chat, socket, onMarkAsRead]);

  useEffect(() => {
    const loadCustomer = async () => {
      if (!chat) return;
      setCustomerLoading(true);
      setCustomerError('');
      setActionFeedback('');
      try {
        const res = await axios.get(`${apiBaseUrl}/chats/${encodeURIComponent(chat.chatId)}/customer`);
        const data = res.data || null;
        setCustomer(data);
        setCustomerForm({
          name: data?.name || chat.name || 'Cliente',
          phone: data?.phone || (chat.chatId ? chat.chatId.split('@')[0] : ''),
          email: data?.email || '',
          document: data?.document || '',
          notes: data?.notes || '',
          address: data?.address || '',
          company_id: data?.company_id || '',
          company_name: data?.company_name || '',
          company_document: data?.company_document || '',
        });
        setTicketAction((prev) => ({
          ...prev,
          gc_reason: data?.gc_reason || '',
        }));
        if (data?.id) {
          const ticketRes = await axios.get(`${apiBaseUrl}/customers/${data.id}/chats`);
          setTickets(ticketRes.data || []);
          const historyRes = await axios.get(`${apiBaseUrl}/customers/${data.id}/notes`);
          setHistoryNotes(historyRes.data || []);
        } else {
          setTickets([]);
          setHistoryNotes([]);
        }

        if (chat?.TicketNumber) {
          const detailRes = await axios.get(`${apiBaseUrl}/tickets/${chat.TicketNumber}`);
          const details = detailRes.data || {};
          setProtocols(details.protocols || []);
          setTicketAction((prev) => ({
            ...prev,
            category: details.category || '',
            resolution_summary: details.resolution_summary || '',
          }));
        } else {
          setProtocols([]);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setCustomer(null);
          setCustomerForm({
            name: chat?.name || 'Cliente',
            phone: chat?.chatId ? chat.chatId.split('@')[0] : '',
            email: '',
            document: '',
            notes: '',
            address: '',
            company_id: '',
            company_name: '',
            company_document: '',
          });
          setTickets([]);
          setHistoryNotes([]);
          setProtocols([]);
        } else {
          setCustomerError('Falha ao carregar cliente.');
        }
      } finally {
        setCustomerLoading(false);
        setIsEditingCustomer(false);
      }
    };

    loadCustomer();
  }, [chat]);

  useEffect(() => {
    const handleUserScroll = () => {
      const container = messagesContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        setUserScrolled(scrollTop + clientHeight < scrollHeight - 4);
      }
    };

    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleUserScroll);
    }

    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleUserScroll);
      }
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!userScrolled && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, userScrolled]); // Adiciona `userScrolled` às dependências

  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;

    if (isInternalNote) {
      socket.emit('send_internal_note', {
        chatId: chat.chatId,
        note: newMessage,
        author: attendantName,
        company_code: localStorage.getItem('companyCode') || undefined,
      });
      setMessages((prev) => [
        ...prev,
        {
          from: 'nota',
          body: newMessage,
          chatId: chat.chatId,
          ticketNumber: chat.TicketNumber,
          senderName: attendantName,
          timestamp: new Date().toISOString(),
          isInternal: true,
        },
      ]);
    } else {
      const messageData = {
        chatId: chat.chatId,
        message: newMessage,
        atendente: attendantName,
        userId: localStorage.getItem('userId') || undefined,
        company_code: localStorage.getItem('companyCode') || undefined,
      };

      socket.emit('send_message', messageData);
      setMessages((prev) => [
        ...prev,
        {
          from: 'atendente',
          body: newMessage,
          chatId: chat.chatId,
          ticketNumber: chat.TicketNumber,
          senderName: attendantName,
          timestamp: new Date().toISOString(),
        },
      ]);
    }

    setNewMessage('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      // Permitir quebra de linha
      return;
    } else if (event.key === 'Enter') {
      event.preventDefault(); // Impede a quebra de linha com Enter
      handleSendMessage(); // Envia a mensagem quando apenas Enter é pressionado
    }
  };

  const handleSendTemplate = (template) => {
    setNewMessage(template);
    setShowTemplates(false);
  };

  const handleAddTemplate = () => {
    if (newTemplate.trim() === '') return;
    const updatedTemplates = [...templates, newTemplate];
    setTemplates(updatedTemplates);
    setNewTemplate('');
    saveTemplates(updatedTemplates);
  };

  const handleEditTemplate = (index) => {
    setNewTemplate(templates[index]);
    setEditTemplateIndex(index);
  };

  const handleSaveEditTemplate = () => {
    if (newTemplate.trim() === '') return;
    const updatedTemplates = [...templates];
    updatedTemplates[editTemplateIndex] = newTemplate;
    setTemplates(updatedTemplates);
    setNewTemplate('');
    setEditTemplateIndex(null);
    saveTemplates(updatedTemplates);
  };

  const handleDeleteTemplate = (index) => {
    const updatedTemplates = templates.filter((_, i) => i !== index);
    setTemplates(updatedTemplates);
    saveTemplates(updatedTemplates);
  };

  const saveTemplates = (templates) => {
    axios.post(`${wppBaseUrl}/save-templates`, { templates }).catch(() => {});
  };

  const handleAddHistoryNote = async () => {
    if (!customer?.id || !newHistoryNote.trim()) return;
    try {
      await axios.post(`${apiBaseUrl}/customers/${customer.id}/notes`, {
        body: newHistoryNote,
        author: attendantName,
      });
      const historyRes = await axios.get(`${apiBaseUrl}/customers/${customer.id}/notes`);
      setHistoryNotes(historyRes.data || []);
      setNewHistoryNote('');
    } catch (error) {
      setCustomerError('Falha ao salvar nota do cliente.');
    }
  };

  const handleEndHumanSupport = async () => {
    if (!chat) return;
    const confirmedCategory = (ticketAction.category || '').trim();
    if (!confirmedCategory) {
      setActionFeedback('Confirme a categoria do chamado antes de finalizar o atendimento.');
      return;
    }

    const confirmed = window.confirm(
      `Confirma a categoria "${confirmedCategory}" para encerrar o atendimento do ticket #${chat.TicketNumber || '-'}?`
    );
    if (!confirmed) return;

    try {
      setActionLoading('finalize');
      setActionFeedback('');
      await axios.post(`${apiBaseUrl}/end-human-support`, {
        chatId: chat.chatId,
        category: confirmedCategory,
        confirm_category: true,
        resolution_summary: (ticketAction.resolution_summary || '').trim() || null,
        atendente: attendantName,
      });
      setActionFeedback('Categoria confirmada. Fluxo de finalização/NPS iniciado.');
    } catch (error) {
      setActionFeedback(error?.response?.data?.message || 'Falha ao finalizar atendimento.');
    } finally {
      setActionLoading('');
    }
  };

  const handleCloseChat = () => {
    onClose();
  };

  const handleTransferSupport = async () => {
    if (!chat?.TicketNumber) {
      setTransferFeedback('Ticket invalido para transferencia.');
      return;
    }
    if (!transferUserId) {
      setTransferFeedback('Selecione um atendente para transferir.');
      return;
    }
    const currentUserId = localStorage.getItem('userId') || '';
    if (transferUserId === currentUserId) {
      setTransferFeedback('Selecione um atendente diferente do atual.');
      return;
    }
    const target = assignableUsers.find((user) => user.email === transferUserId);
    const targetAttendantName = target?.name || target?.email || transferUserId;
    try {
      setTransferLoading(true);
      setTransferFeedback('');
      await axios.post(`${apiBaseUrl}/chats/${encodeURIComponent(chat.TicketNumber)}/transfer-support`, {
        target_user_id: transferUserId,
        target_attendant: targetAttendantName,
        from_attendant: attendantName,
        requester_user_id: currentUserId,
      });
      setTransferFeedback(`Atendimento transferido para ${targetAttendantName}.`);
      setShowTransferPanel(false);
    } catch (error) {
      setTransferFeedback(error?.response?.data?.message || 'Falha ao transferir atendimento.');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleCustomerField = (e) => {
    const { name, value } = e.target;
    setCustomerForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCustomer = async () => {
    setCustomerLoading(true);
    setCustomerError('');
    try {
      const payload = {
        ...customerForm,
        customer_id: customer?.id || null,
        chatId: chat.chatId,
        company_id: customerForm.company_id || null,
      };
      await axios.post(`${apiBaseUrl}/customers/intake`, payload);
      const refreshed = await axios.get(`${apiBaseUrl}/chats/${encodeURIComponent(chat.chatId)}/customer`);
      setCustomer(refreshed.data);
      if (refreshed.data?.id) {
        const ticketRes = await axios.get(`${apiBaseUrl}/customers/${refreshed.data.id}/chats`);
        setTickets(ticketRes.data || []);
        const historyRes = await axios.get(`${apiBaseUrl}/customers/${refreshed.data.id}/notes`);
        setHistoryNotes(historyRes.data || []);
      }
      await loadCompanies();
      setIsEditingCustomer(false);
    } catch (error) {
      setCustomerError('Falha ao salvar cliente.');
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleTicketActionField = (event) => {
    const { name, value } = event.target;
    setTicketAction((prev) => ({ ...prev, [name]: value }));
  };

  const refreshProtocols = async () => {
    if (!chat?.TicketNumber) return;
    try {
      const protocolRes = await axios.get(`${apiBaseUrl}/tickets/${chat.TicketNumber}/protocols`);
      setProtocols(protocolRes.data || []);
    } catch (error) {
      setProtocols([]);
    }
  };

  const refreshCustomerForChat = async () => {
    try {
      const refreshed = await axios.get(`${apiBaseUrl}/chats/${encodeURIComponent(chat.chatId)}/customer`);
      setCustomer(refreshed.data || null);
      if (refreshed.data) {
        setCustomerForm({
          name: refreshed.data?.name || chat.name || 'Cliente',
          phone: refreshed.data?.phone || (chat.chatId ? chat.chatId.split('@')[0] : ''),
          email: refreshed.data?.email || '',
          document: refreshed.data?.document || '',
          notes: refreshed.data?.notes || '',
          address: refreshed.data?.address || '',
          company_id: refreshed.data?.company_id || '',
          company_name: refreshed.data?.company_name || '',
          company_document: refreshed.data?.company_document || '',
        });
        setTicketAction((prev) => ({
          ...prev,
          gc_reason: refreshed.data?.gc_reason || prev.gc_reason || '',
        }));
      }
    } catch (error) {
      // noop
    }
  };

  const handleResolveWithNps = async () => {
    if (!chat?.TicketNumber) return;
    try {
      setActionLoading('resolve');
      setActionFeedback('');
      await axios.post(`${apiBaseUrl}/tickets/${chat.TicketNumber}/resolve`, {
        category: ticketAction.category,
        resolution_summary: ticketAction.resolution_summary,
        atendente: attendantName,
      });
      setActionFeedback('Chamado resolvido e fluxo de NPS iniciado.');
    } catch (error) {
      setActionFeedback(error.response?.data?.message || 'Falha ao resolver chamado.');
    } finally {
      setActionLoading('');
    }
  };

  const handleOpenProtocol = async (type) => {
    if (!chat?.TicketNumber) return;
    try {
      setActionLoading(type.toLowerCase());
      setActionFeedback('');
      const response = await axios.post(`${apiBaseUrl}/tickets/${chat.TicketNumber}/protocols`, {
        type,
        details: ticketAction.protocol_details || ticketAction.resolution_summary,
        author: attendantName,
      });
      const protocolCode = response.data?.protocol_code;
      setActionFeedback(`Protocolo ${type} criado${protocolCode ? `: ${protocolCode}` : ''}.`);
      await refreshProtocols();
    } catch (error) {
      setActionFeedback(error.response?.data?.message || `Falha ao abrir protocolo ${type}.`);
    } finally {
      setActionLoading('');
    }
  };

  const handleToggleCrisis = async () => {
    if (!customer?.id) {
      setActionFeedback('Salve os dados do cliente antes de marcar GC.');
      return;
    }
    try {
      setActionLoading('gc');
      setActionFeedback('');
      await axios.put(`${apiBaseUrl}/customers/${customer.id}/crisis`, {
        is_gc: !customer?.is_gc,
        gc_reason: ticketAction.gc_reason,
      });
      await refreshCustomerForChat();
      setActionFeedback(!customer?.is_gc ? 'Cliente marcado como Gestão de Crise.' : 'Cliente removido da Gestão de Crise.');
    } catch (error) {
      setActionFeedback(error.response?.data?.message || 'Falha ao atualizar GC.');
    } finally {
      setActionLoading('');
    }
  };

  const handleClickOutside = (event) => {
    if (templateRef.current && !templateRef.current.contains(event.target)) {
      setShowTemplates(false);
    }
  };

  useEffect(() => {
    if (showTemplates) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTemplates]);

  const renderMedia = (message) => {
    if (typeof message.body !== 'string') {
      return message.body;
    }

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = message.body.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        if (part.match(/\.(jpeg|jpg|gif|png)$/) !== null) {
          return <img key={index} src={part} alt="imagem" className="chat-media" />;
        } else if (part.match(/\.(mp4|webm)$/) !== null) {
          return (
            <video key={index} controls className="chat-media">
              <source src={part} type="video/mp4" />
              Seu navegador não suporta a tag de vídeo.
            </video>
          );
        } else if (part.match(/\.(mp3|ogg)$/) !== null) {
          return (
            <audio key={index} controls className="chat-media">
              <source src={part} type="audio/mp3" />
              Seu navegador não suporta a tag de áudio.
            </audio>
          );
        } else {
          return <a key={index} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
        }
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!chat) {
    return <div className="chat-window">Selecione um chat para começar</div>;
  }

  const displayName = customer?.name || chat.customer_name || chat.name || 'Cliente';
  const displayPhone = customer?.phone || chat.customer_phone || (chat.chatId ? chat.chatId.split('@')[0] : '');
  const companyName = (
    customer?.company_name
    || companies.find((c) => String(c.id) === String(customerForm.company_id))?.name
    || customerForm.company_name
  );

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <span className="name">{displayName}</span>
          <span className="chat-header-phone">{displayPhone || 'Sem telefone'}</span>
        </div>
        <div className="header-buttons">
          <button
            className="transfer-button"
            onClick={() => {
              setTransferFeedback('');
              setShowTransferPanel((prev) => !prev);
            }}
          >
            Transferir
          </button>
          <button className="end-support-button" onClick={handleEndHumanSupport} disabled={actionLoading !== ''}>
            {actionLoading === 'finalize' ? 'Finalizando...' : 'Finalizar Atendimento'}
          </button>
          <button className="close-button" onClick={handleCloseChat}>Fechar Chat</button>
        </div>
        {showTransferPanel && (
          <div className="transfer-panel">
            <label htmlFor="transfer-user-select">Transferir para</label>
            <select
              id="transfer-user-select"
              value={transferUserId}
              onChange={(event) => setTransferUserId(event.target.value)}
              disabled={transferLoading}
            >
              <option value="">Selecione um atendente</option>
              {assignableUsers.map((user) => (
                <option key={user.email} value={user.email}>
                  {user.name || user.email} ({user.role || 'atendente'})
                </option>
              ))}
            </select>
            <div className="transfer-panel-actions">
              <button onClick={handleTransferSupport} disabled={transferLoading || !transferUserId}>
                {transferLoading ? 'Transferindo...' : 'Confirmar'}
              </button>
              <button
                className="ghost"
                onClick={() => {
                  setShowTransferPanel(false);
                  setTransferFeedback('');
                }}
                disabled={transferLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
        {transferFeedback && <p className="transfer-feedback">{transferFeedback}</p>}
      </div>
      <div className="chat-body">
        <div className="chat-main">
          <div className="chat-messages" ref={messagesContainerRef}>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`chat-message ${message.isInternal || message.from === 'nota' ? 'from-internal' : (message.from === 'assistente' ? 'from-assistant' : (message.from === 'atendente' ? 'from-attendant' : 'from-cliente'))}`}
              >
                <div className="message-sender">
                  {message.from === 'nota' || message.isInternal ? `Nota interna - ${message.senderName || attendantName}` : (message.from === 'assistente' ? 'Robô' : message.from === 'atendente' ? 'Atendente' : message.senderName || 'Cliente')}:
                </div>
                {renderMedia(message)}
                <div className="message-timestamp">
                  {new Date(message.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input">
            <div className="note-toggle">
              <button className={!isInternalNote ? 'active' : ''} onClick={() => setIsInternalNote(false)}>Mensagem</button>
              <button className={isInternalNote ? 'active' : ''} onClick={() => setIsInternalNote(true)}>Nota Interna</button>
            </div>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isInternalNote ? 'Escreva uma nota interna (não enviada ao cliente)' : 'Digite uma mensagem'}
              rows="1"
            />
            <button onClick={handleSendMessage}>{isInternalNote ? 'Salvar' : 'Enviar'}</button>
            <button className="templates-button" onClick={() => setShowTemplates(!showTemplates)}>Templates</button>
            {showTemplates && (
              <div className="templates-dropdown" ref={templateRef}>
                {templates.map((template, index) => (
                  <div key={index} className="template-item">
                    <span onClick={() => handleSendTemplate(template)}>{template}</span>
                    <button onClick={() => handleEditTemplate(index)}>Editar</button>
                    <button onClick={() => handleDeleteTemplate(index)}>Excluir</button>
                  </div>
                ))}
                <div className="template-input">
                  <input
                    type="text"
                    value={newTemplate}
                    onChange={(e) => setNewTemplate(e.target.value)}
                    placeholder="Novo template"
                  />
                  {editTemplateIndex !== null ? (
                    <button onClick={handleSaveEditTemplate}>Salvar</button>
                  ) : (
                    <button onClick={handleAddTemplate}>Adicionar</button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="chat-side">
          <div className="chat-side-card">
            <div className="chat-side-header">
              <h4>Cliente</h4>
              <div className="chat-side-header-tags">
                {customer?.is_gc && <span className="chat-side-crisis">GC</span>}
                {companyName && <span className="chat-side-company">{companyName}</span>}
              </div>
            </div>
            <div className="chat-side-meta">
              <div><strong>Nome:</strong> {displayName}</div>
              <div><strong>Telefone:</strong> {displayPhone || 'Sem telefone'}</div>
              <div><strong>Empresa:</strong> {companyName || 'Sem empresa'}</div>
            </div>
            <div className="chat-side-actions">
              <button onClick={() => setIsEditingCustomer((prev) => !prev)}>
                {isEditingCustomer ? 'Fechar edição' : 'Editar dados'}
              </button>
              {customerError && <span className="chat-customer-error">{customerError}</span>}
            </div>
            {isEditingCustomer && (
              <div className="chat-customer-form">
                <input name="name" placeholder="Nome" value={customerForm.name} onChange={handleCustomerField} />
                <input name="phone" placeholder="Telefone" value={customerForm.phone} onChange={handleCustomerField} />
                <input name="email" placeholder="Email" value={customerForm.email} onChange={handleCustomerField} />
                <input name="document" placeholder="Documento" value={customerForm.document} onChange={handleCustomerField} />
                <input name="address" placeholder="Endereço" value={customerForm.address} onChange={handleCustomerField} />
                <select name="company_id" value={customerForm.company_id} onChange={handleCustomerField}>
                  <option value="">Empresa (opcional)</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
                <input
                  name="company_name"
                  placeholder="Nova empresa (se não cadastrada)"
                  value={customerForm.company_name}
                  onChange={handleCustomerField}
                />
                <input
                  name="company_document"
                  placeholder="CNPJ da nova empresa"
                  value={customerForm.company_document}
                  onChange={handleCustomerField}
                />
                <textarea name="notes" placeholder="Observações" value={customerForm.notes} onChange={handleCustomerField} rows="2" />
                <button onClick={handleSaveCustomer} disabled={customerLoading}>
                  {customerLoading ? 'Salvando...' : 'Salvar Cliente'}
                </button>
              </div>
            )}
          </div>
          <div className="chat-side-card">
            <div className="chat-side-header">
              <h4>Ações do Chamado</h4>
              <span>#{chat.TicketNumber}</span>
            </div>
            <div className="chat-side-actions-form">
              <input
                name="category"
                placeholder="Categoria confirmada"
                value={ticketAction.category}
                onChange={handleTicketActionField}
              />
              <textarea
                name="resolution_summary"
                rows="2"
                placeholder="Resumo de resolução"
                value={ticketAction.resolution_summary}
                onChange={handleTicketActionField}
              />
              <textarea
                name="protocol_details"
                rows="2"
                placeholder="Contexto para COR/RMA (opcional)"
                value={ticketAction.protocol_details}
                onChange={handleTicketActionField}
              />
              <textarea
                name="gc_reason"
                rows="2"
                placeholder="Motivo da Gestão de Crise (GC)"
                value={ticketAction.gc_reason}
                onChange={handleTicketActionField}
              />
            </div>
            <div className="chat-side-actions-grid">
              <button onClick={handleResolveWithNps} disabled={actionLoading !== ''}>
                {actionLoading === 'resolve' ? 'Processando...' : 'Resolver + NPS'}
              </button>
              <button className="ghost" onClick={() => handleOpenProtocol('COR')} disabled={actionLoading !== ''}>
                {actionLoading === 'cor' ? 'Abrindo...' : 'Abrir COR'}
              </button>
              <button className="ghost" onClick={() => handleOpenProtocol('RMA')} disabled={actionLoading !== ''}>
                {actionLoading === 'rma' ? 'Abrindo...' : 'Abrir RMA'}
              </button>
              <button className="warn" onClick={handleToggleCrisis} disabled={actionLoading !== ''}>
                {actionLoading === 'gc'
                  ? 'Salvando...'
                  : (customer?.is_gc ? 'Remover GC' : 'Marcar GC')}
              </button>
            </div>
            {actionFeedback && <p className="chat-side-feedback">{actionFeedback}</p>}
            <div className="chat-side-protocols">
              <h5>Protocolos</h5>
              {protocols.length === 0 && <p>Nenhum protocolo aberto para este ticket.</p>}
              {protocols.map((protocol) => (
                <div key={protocol.id} className="chat-side-protocol-item">
                  <div className="chat-side-protocol-row">
                    <strong>{protocol.protocol_code}</strong>
                    <span>{protocol.protocol_type}</span>
                  </div>
                  <small>
                    Projeto: {protocol.project_code || '-'}
                    {protocol.created_at ? ` · ${new Date(protocol.created_at).toLocaleString()}` : ''}
                  </small>
                </div>
              ))}
            </div>
          </div>
          <div className="chat-side-card">
            <div className="chat-side-header">
              <h4>Histórico de Tickets</h4>
              <span>{tickets.length} registros</span>
            </div>
            <div className="chat-side-tickets">
              {tickets.length === 0 && <p>Sem tickets anteriores.</p>}
              {tickets.map((ticket) => (
                <div key={`${ticket.chatId}-${ticket.TicketNumber}`} className="chat-side-ticket">
                  <div className="chat-side-ticket-row">
                    <strong>#{ticket.TicketNumber}</strong>
                    <span className={`ticket-status status-${ticket.status}`}>
                      {getSupportStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <small>{ticket.lastMessage || 'Sem mensagem'}</small>
                </div>
              ))}
            </div>
            <div className="chat-side-history">
              <h5>Histórico do Cliente</h5>
              <div className="chat-side-history-editor">
                <textarea
                  placeholder="Adicionar anotação interna do cliente"
                  value={newHistoryNote}
                  onChange={(e) => setNewHistoryNote(e.target.value)}
                  rows="2"
                />
                <button onClick={handleAddHistoryNote}>Salvar anotação</button>
              </div>
              {historyNotes.length === 0 && <p>Sem registros anteriores.</p>}
              {historyNotes.map((note) => (
                <div key={note.id} className="chat-side-history-item">
                  <div className="chat-side-history-meta">
                    <span>{note.author || 'Sistema'}</span>
                    <small>{note.created_at ? new Date(note.created_at).toLocaleString() : ''}</small>
                  </div>
                  <p>{note.body}</p>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ChatWindow;
